import React, { useMemo } from 'react';
import SegmentedPolylineOverlay from './SegmentedPolylineOverlay';

// Fixed color mapping for each known PWM mode
const pwmModeColorMap = {
  'Spot Plain': '#1976d2',      // blue
  'Spot Spraying': '#fbc02d',   // yellow
  'Broadcast': '#f57c00',       // orange
  'Dual Rate': '#7b1fa2',       // purple
  'SiC Plain': '#00897b',       // teal
  'SiC Spot': '#c2185b',        // pink
  'SiC Broadcast': '#455a64',   // gray
  'SiC Dual Rate': '#afb42b',   // lime
};

// List of all possible modes in preferred order
const allModes = [
  'Spot Plain',
  'Spot Spraying',
  'Broadcast',
  'Dual Rate',
  'SiC Plain',
  'SiC Spot',
  'SiC Broadcast',
  'SiC Dual Rate',
];

// Add Weed-It Off as the first range if needed
function getRangesWithOff(allPoints, uniqueModes) {
  const hasOff = allPoints.some(pt => pt.weedItOff);
  // Only include modes that appear in the data, but always use the fixed color and display name
  const modeRanges = allModes
    .filter(mode => uniqueModes.includes(mode))
    .map(mode => ({
      min: mode,
      max: mode,
      color: pwmModeColorMap[mode],
      value: mode,
    }));
  if (hasOff) {
    return [
      {
        min: 'off',
        max: 'off',
        color: '#d32f2f',
        value: 'Weed-It Off',
        dashed: true,
      },
      ...modeRanges
    ];
  }
  return modeRanges;
}

export default function WeedItPwmModeOverlay({ routes }) {
  // routes: array of segments (arrays of points)
  const allPoints = useMemo(() => routes.flat(), [routes]);
  // Get all unique, non-empty PWM mode values from the route
  const uniqueModes = useMemo(() => {
    const set = new Set();
    allPoints.forEach(pt => {
      if (pt.weedItPwmMode && typeof pt.weedItPwmMode === 'string') {
        set.add(pt.weedItPwmMode);
      }
    });
    return Array.from(set);
  }, [allPoints]);

  const finalRanges = useMemo(() => getRangesWithOff(allPoints, uniqueModes), [allPoints, uniqueModes]);

  // If weedItOff is true, treat as "off" (value 'off')
  const valueSelector = pt =>
    pt.weedItOff ? 'off' : pt.weedItPwmMode;

  // Legend formatter for display name
  const legendFormatter = r => r.value;

  if (!allPoints.length) return null;

  return (
    <SegmentedPolylineOverlay
      segments={routes}
      valueSelector={valueSelector}
      ranges={finalRanges}
      legendTitle="Spray Mode"
      legendFormatter={legendFormatter}
      units=""
      discrete={true}
    />
  );
}